import { useEffect, useState, type ReactNode } from 'react';
import {
  afkoppelStatus,
  totaleBergingM3,
  type Foto,
  type Registratie,
  type TuinAdvies,
} from '@/types/domain';
import { adresVolledig, datumNL, getalNL } from '@/utils/format';
import { isHuurwoning } from '@/data/huurwoningen';
import { getFotoService } from '@/services/serviceFactory';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { VergrootIcoon } from '@/components/layout/Iconen';
import { FotoViewer } from './FotoViewer';
import './form.css';

interface RegistratieOverzichtProps {
  registratie: Registratie;
}

/**
 * Alleen-lezen weergave voor de gemeente.
 *
 * Bewust geen uitgeschakeld formulier maar een eigen weergave: een scherm vol
 * grijze invoervelden nodigt uit tot klikken en leest slechter. Dit is puur
 * om te bekijken.
 *
 * De persoonsgegevens van de bewoner staan hier niet in. Ze komen ook niet
 * mee uit de database: de gemeente leest uit een weergave waarin die kolommen
 * niet bestaan.
 */
export function RegistratieOverzicht({ registratie }: RegistratieOverzichtProps): JSX.Element {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [bekekenIndex, setBekekenIndex] = useState<number | null>(null);

  useEffect(() => {
    let actief = true;
    const dienst = getFotoService();

    Promise.all(
      registratie.fotos.map(async (foto) => {
        try {
          return [foto.id, await dienst.weergaveUrl(foto)] as const;
        } catch {
          return [foto.id, ''] as const;
        }
      })
    ).then((paren) => {
      if (actief) setUrls(Object.fromEntries(paren));
    });

    return () => {
      actief = false;
    };
  }, [registratie.fotos]);

  const berging = totaleBergingM3(registratie);

  return (
    <div className="paneel__inhoud">
      {/* Zeggen wat je stand is, niet laten merken door velden die niet
          reageren. Grijze invoervelden nodigen uit tot klikken en leggen
          nooit uit waarom er niets gebeurt. */}
      <div className="overzicht__balk">
        <strong>Je kijkt mee</strong>
        <span>
          Je kunt alles lezen en exporteren, maar niets wijzigen. Persoonsgegevens van
          bewoners zitten niet in deze weergave.
        </span>
      </div>

      <div className="overzicht__status">
        <StatusBadge status={afkoppelStatus(registratie, isHuurwoning(registratie.adres))} />
      </div>

      <Blok titel="Woning">
        <Regel label="Adres" waarde={adresVolledig(registratie.adres)} />
        <Regel label="BAG-id" waarde={registratie.bagId} />
        <Regel label="Koop of huur" waarde={registratie.eigendom} />
      </Blok>

      <Blok titel="Bezoek">
        <Regel label="Datum" waarde={datumNL(registratie.datumBezoek)} />
        <Regel label="Afkoppelcoach" waarde={registratie.coach} />
        <Regel label="Uitkomst" waarde={registratie.uitkomstBezoek} />
        <Regel label="Toelichting" waarde={registratie.uitkomstToelichting} breed />
      </Blok>

      <TuinBlok titel="Voortuin" tuin={registratie.voortuin} />
      <TuinBlok titel="Achtertuin" tuin={registratie.achtertuin} />

      <Blok titel="Resultaat">
        {/* Twee regels met elk hun eigen onderwerp, net als bij een stip op de
            kaart. Bij huur is de eerste geen keuze maar een projectgegeven. */}
        <Regel
          label="Afkoppelen van het pand"
          waarde={
            isHuurwoning(registratie.adres)
              ? 'De corporatie koppelt dit pand af'
              : registratie.afkoppelbesluit
          }
        />
        <Regel label="Extra maatregelen" waarde={registratie.maatregelenbesluit} />
        <Regel label="Subsidieaanvraag" waarde={registratie.subsidieStatus} />
        <Regel label="Realisatie" waarde={registratie.realisatieStatus} />
        <Regel label="Vervolgafspraak" waarde={datumNL(registratie.datumVervolg)} />
        {berging > 0 && (
          <Regel label="Totale berging" waarde={`${getalNL(berging, 1)} m³`} />
        )}
        <Regel label="Toelichting" waarde={registratie.algemeneToelichting} breed />
      </Blok>

      {registratie.fotos.length > 0 && (
        <Blok titel={`Foto's (${registratie.fotos.length})`}>
          <div className="fotos">
            {registratie.fotos.map((foto: Foto, index: number) => (
              <figure key={foto.id} className="foto">
                {/* Het beeldvlak is het ankerpunt voor alles wat erop ligt.
                    Zonder deze laag valt de knop eruit en dekt hij het hele
                    paneel af, want hij is absoluut geplaatst. */}
                <div className="foto__beeld">
                  {urls[foto.id] ? (
                    <button
                      type="button"
                      className="foto__open"
                      onClick={() => setBekekenIndex(index)}
                      aria-label={`Foto ${foto.soort} groot bekijken`}
                    >
                      <img
                        src={urls[foto.id]}
                        alt={foto.toelichting || `${foto.soort}: ${foto.bestandsnaam}`}
                        loading="lazy"
                      />
                      <span className="foto__soortcapsule">{foto.soort}</span>
                      <span className="foto__vergroot" aria-hidden="true">
                        <VergrootIcoon />
                      </span>
                    </button>
                  ) : (
                    <div className="foto__laden" aria-hidden="true" />
                  )}
                </div>

                {foto.toelichting && (
                  <figcaption className="foto__bijschrift">
                    <span className="foto__toelichting">{foto.toelichting}</span>
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </Blok>
      )}

      <p className="overzicht__voet">
        Laatst gewijzigd op {datumNL(registratie.gewijzigdOp)}.
      </p>

      {bekekenIndex !== null && (
        <FotoViewer
          fotos={registratie.fotos}
          index={bekekenIndex}
          urls={urls}
          bijSluiten={() => setBekekenIndex(null)}
          bijWisselen={setBekekenIndex}
        />
      )}
    </div>
  );
}

function Blok({ titel, children }: { titel: string; children: ReactNode }): JSX.Element {
  return (
    <section className="overzicht__blok">
      <h3 className="overzicht__titel">{titel}</h3>
      <dl className="overzicht__lijst">{children}</dl>
    </section>
  );
}

function Regel({
  label,
  waarde,
  breed = false,
}: {
  label: string;
  waarde: string;
  breed?: boolean;
}): JSX.Element | null {
  // Lege velden weglaten in plaats van streepjes tonen: dat leest rustiger.
  if (!waarde) return null;

  return (
    <div className={`overzicht__regel${breed ? ' overzicht__regel--breed' : ''}`}>
      <dt>{label}</dt>
      <dd>{waarde}</dd>
    </div>
  );
}

function TuinBlok({ titel, tuin }: { titel: string; tuin: TuinAdvies }): JSX.Element {
  if (!tuin.aanwezig) {
    return (
      <Blok titel={titel}>
        <Regel label="Aanwezig" waarde="Nee" />
        <Regel label="Toelichting" waarde={tuin.toelichting} breed />
      </Blok>
    );
  }

  return (
    <Blok titel={titel}>
      <Regel label="Advies" waarde={tuin.advies.join(', ')} breed />
      <Regel
        label="Indicatieve berging"
        waarde={tuin.bergingM3 !== null ? `${getalNL(tuin.bergingM3, 1)} m³` : ''}
      />
      <Regel label="Toelichting" waarde={tuin.toelichting} breed />
    </Blok>
  );
}
